import React from 'react';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import { X } from 'react-bootstrap-icons';
import FilterValueInput from './FilterValueInput';
import {
  FilterOperator,
  ReportFieldMetadata,
  ReportFilter,
  getOperatorsForValueType
} from '../../types/report.types';

interface Props {
  filter: ReportFilter;
  fields: ReportFieldMetadata[];
  onUpdate: (filter: ReportFilter) => void;
  onRemove: () => void;
}

const OPERATOR_LABELS: Record<FilterOperator, string> = {
  Equals: 'Equals',
  NotEquals: 'Not Equals',
  Contains: 'Contains',
  GreaterThan: 'Greater Than',
  LessThan: 'Less Than',
  IsEmpty: 'Is Empty',
  IsNotEmpty: 'Is Not Empty'
};

function FilterRow({ filter, fields, onUpdate, onRemove }: Props) {
  const selectedField = fields.find((f) => f.fieldName === filter.field);
  const availableOperators = selectedField
    ? getOperatorsForValueType(selectedField.valueType)
    : (['Equals', 'NotEquals'] as FilterOperator[]);

  const handleFieldChange = (fieldName: string) => {
    const newField = fields.find((f) => f.fieldName === fieldName);
    const newOperators = newField
      ? getOperatorsForValueType(newField.valueType)
      : (['Equals', 'NotEquals'] as FilterOperator[]);

    // Reset operator and value when field changes
    onUpdate({
      ...filter,
      field: fieldName,
      operator: newOperators[0],
      value: ''
    });
  };

  const handleOperatorChange = (operator: FilterOperator) => {
    // Clear value if changing to IsEmpty/IsNotEmpty
    const newValue = operator === 'IsEmpty' || operator === 'IsNotEmpty' ? '' : filter.value;
    onUpdate({
      ...filter,
      operator,
      value: newValue
    });
  };

  const handleValueChange = (value: string) => {
    onUpdate({
      ...filter,
      value
    });
  };

  return (
    <Row className="mb-2 align-items-center">
      <Col xs={12} md={4}>
        <Form.Select
          value={filter.field}
          onChange={(e) => handleFieldChange(e.target.value)}
          size="sm">
          <option value="">Select field...</option>
          {fields.map((field) => (
            <option key={field.fieldName} value={field.fieldName}>
              {field.displayName}
            </option>
          ))}
        </Form.Select>
      </Col>
      <Col xs={12} md={3}>
        <Form.Select
          value={filter.operator}
          onChange={(e) => handleOperatorChange(e.target.value as FilterOperator)}
          size="sm"
          disabled={!filter.field}>
          {availableOperators.map((op) => (
            <option key={op} value={op}>
              {OPERATOR_LABELS[op]}
            </option>
          ))}
        </Form.Select>
      </Col>
      <Col xs={12} md={4}>
        <FilterValueInput
          field={selectedField}
          operator={filter.operator}
          value={filter.value}
          onChange={handleValueChange}
        />
      </Col>
      <Col xs={12} md={1}>
        <Button variant="outline-danger" size="sm" onClick={onRemove} title="Remove filter">
          <X size={16} />
        </Button>
      </Col>
    </Row>
  );
}

export default FilterRow;
